/**
 * UnsavedChangesModal
 *
 * Shown when the user has unsaved notification changes and attempts
 * to navigate away. Matches the "Popup / Leave Ritual" Figma design
 * at node-id=13053-41317.
 */

import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/language"

interface UnsavedChangesModalProps {
  isOpen: boolean
  onSaveAndLeave: () => void
  onLeaveWithoutSaving: () => void
  onCancel: () => void
  isSaving?: boolean
}

export function UnsavedChangesModal({
  isOpen,
  onSaveAndLeave,
  onLeaveWithoutSaving,
  onCancel,
  isSaving = false,
}: UnsavedChangesModalProps) {
  const { t } = useTranslation("common")

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent
        showCloseButton={false}
        overlayClass="bg-[#020202E5]"
        className="w-[600px] max-w-[calc(100vw-2rem)] gap-0 p-0 !rounded-tl-none !rounded-tr-[30px] !rounded-br-none !rounded-bl-[30px]"
        style={{
          background: "rgba(200, 103, 87, 0.05)",
          backdropFilter: "blur(50px)",
          WebkitBackdropFilter: "blur(50px)",
          border: "1px solid rgba(200, 103, 87, 0.5)",
          boxShadow: "none",
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="absolute right-5 top-5 z-10 w-10 h-10 flex items-center justify-center rounded-full border  backdrop-blur-md cursor-pointer transition-opacity focus:outline-none border-gradient-3d"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>

        <div className="flex flex-col items-center md:px-[120px] pt-[60px] pb-10">
          <DialogHeader className="text-center gap-0 sm:text-center w-full">
            <DialogTitle
              className="text-[#F2F0EB]/90 text-[20px] md:text-[24px] font-semibold text-center leading-[29px] tracking-normal"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {t("unsavedChangesModal.title")}
            </DialogTitle>
            <DialogDescription
              className="text-[#F2F0EB] text-sm font-normal text-center leading-[25px] mt-2 md:mt-5"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {t("unsavedChangesModal.description")}
            </DialogDescription>
          </DialogHeader>

          {/* Buttons */}
          <div className="flex flex-col gap-[15px] mt-[20px] md:mt-[55px] w-[280px]">
            <Button
              variant="ghost"
              size="lg"
              onClick={onSaveAndLeave}
              disabled={isSaving}
              isLoading={isSaving}
              className="w-full h-[50px] rounded-[100px] bg-[#F2F0EB] text-[#020202] font-semibold text-sm uppercase tracking-normal border-0"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {t("unsavedChangesModal.saveChanges")}
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={onLeaveWithoutSaving}
              className="w-full h-[50px] rounded-[100px] bg-transparent border border-[#F2F0EB] text-[#F2F0EB] font-semibold text-sm uppercase tracking-normal"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {t("unsavedChangesModal.leaveWithoutSaving")}
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={onCancel}
              className="w-full h-[50px] rounded-[100px] bg-transparent border border-[#F2F0EB] text-[#F2F0EB] font-semibold text-sm tracking-normal normal-case uppercase"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {t("unsavedChangesModal.leaveAnyway")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
